/**
 * Calendar layout helpers — computes Google-Calendar style "continuous bar"
 * placement for multi-day events within a row of N day columns.
 *
 * Why this exists
 * ---------------
 * Across the app we have several calendar surfaces (admin Job Calendar, full
 * Schedule page, Operative Schedule, Dispatch Board, Dashboard) that all need
 * to render multi-day jobs as ONE continuous bar that spans every day cell
 * the job occupies — staying in the SAME vertical "slot" across all days.
 * Previously each day cell stacked its own pills independently, which broke
 * visual continuity. This helper centralises the layout maths so each surface
 * can render a uniform bar overlay.
 *
 * Input events use inclusive YYYY-MM-DD strings for `startISO`/`endISO`.
 * Extra fields (id, title, status, etc.) pass straight through on `event`.
 */

/**
 * Layout a single row of N day columns.
 *
 * @param {string[]} rowDays - ISO YYYY-MM-DD dates ordered left → right.
 * @param {Array<{id:string, startISO:string, endISO?:string, startTime?:string}>} events
 *        Events with inclusive startISO/endISO YYYY-MM-DD strings. Pass any
 *        extra fields you need (they are returned via `bar.event`).
 * @returns {{ bars: Array, maxSlot: number }}
 *   `bars[i]` is { event, startCol, span, slot, continuesLeft, continuesRight,
 *   isMultiDay, clampedStartISO, clampedEndISO }.
 *   `startCol`/`span` are 1-based CSS grid column values.
 *   `slot` is 0-based row index within the row (0 = top slot).
 *   `maxSlot` is the highest slot index used (or -1 if no bars).
 */
export function computeRowBars(rowDays, events) {
  if (!rowDays?.length || !events?.length) return { bars: [], maxSlot: -1 };
  const first = rowDays[0];
  const last = rowDays[rowDays.length - 1];
  const colIndex = new Map(rowDays.map((d, i) => [d, i + 1]));

  const inRange = [];
  for (const e of events) {
    if (!e.startISO) continue;
    const endISO = e.endISO || e.startISO;
    // Skip events fully outside this row's date window
    if (endISO < first || e.startISO > last) continue;
    const clampedStart = e.startISO < first ? first : e.startISO;
    const clampedEnd = endISO > last ? last : endISO;
    const startCol = colIndex.get(clampedStart);
    const endCol = colIndex.get(clampedEnd);
    if (!startCol || !endCol) continue;
    inRange.push({
      event: e,
      startCol,
      span: endCol - startCol + 1,
      continuesLeft: e.startISO < first,
      continuesRight: endISO > last,
      isMultiDay: e.startISO !== endISO,
      clampedStartISO: clampedStart,
      clampedEndISO: clampedEnd,
    });
  }

  // Sort so the layout is stable and predictable:
  //   1. earlier start column first  → events fill left-to-right
  //   2. longer span first           → multi-day bars claim low slots
  //   3. earlier start time / id     → deterministic between identical spans
  inRange.sort((a, b) => {
    if (a.startCol !== b.startCol) return a.startCol - b.startCol;
    if (b.span !== a.span) return b.span - a.span;
    const at = a.event.startTime || "";
    const bt = b.event.startTime || "";
    if (at !== bt) return at.localeCompare(bt);
    return String(a.event.id || "").localeCompare(String(b.event.id || ""));
  });

  // Greedy slot assignment — slot is "free" for an event if the slot's
  // currently-occupied column range doesn't overlap the event's column range.
  const slotEndCol = []; // slotEndCol[s] = last column occupied in slot `s`
  let maxSlot = -1;
  const bars = inRange.map((b) => {
    let slot = 0;
    while (slot < slotEndCol.length && slotEndCol[slot] >= b.startCol) slot++;
    slotEndCol[slot] = b.startCol + b.span - 1;
    if (slot > maxSlot) maxSlot = slot;
    return { ...b, slot };
  });

  return { bars, maxSlot };
}

/**
 * Layout an entire month grid (42 cells = 6 rows × 7 days) as 6 independent
 * weekly rows. A multi-day event that crosses a Sunday → Monday boundary is
 * split into two bars (one per row) automatically by `computeRowBars`.
 *
 * @param {string[]} allCells - 42 ISO YYYY-MM-DD strings, week-by-week.
 * @param {Array} events - see computeRowBars
 * @returns {Array<{rowDays:string[], bars:Array, maxSlot:number}>}
 */
export function computeMonthRows(allCells, events) {
  const rows = [];
  for (let i = 0; i < allCells.length; i += 7) {
    const rowDays = allCells.slice(i, i + 7);
    rows.push({ rowDays, ...computeRowBars(rowDays, events) });
  }
  return rows;
}

/**
 * Per-bar fixed height in pixels — kept here so every surface lines up to the
 * same grid metric. Slightly larger than the original pills because bars now
 * need to be a uniform height to align across cells.
 */
export const BAR_HEIGHT_PX = 22;

/**
 * Vertical gap between bars (px).
 */
export const BAR_GAP_PX = 2;

/**
 * Compute the inline grid-template-rows value for a row container that holds
 * `slots` bar slots. The first row is a fixed header row that reserves space
 * for the day-number / day-of-week label that lives inside each day cell, the
 * middle rows are fixed-height bar slots, and the last row absorbs any
 * remaining vertical space (1fr) so the cells fill the row evenly.
 *
 * @param {number} slots - number of bar slots (= maxSlot + 1, min 1)
 * @param {number} [headerPx=28] - pixels reserved for the day-number header
 * @returns {string}  CSS grid-template-rows value
 */
export function rowGridTemplateRows(slots, headerPx = 28) {
  const s = Math.max(1, slots);
  return `${headerPx}px repeat(${s}, ${BAR_HEIGHT_PX}px) 1fr`;
}
